import { useEffect, useState } from "react";
import { Check, Timer, X } from "lucide-react";
import { countdown } from "./countdown";

export const GOALS = [["conversations", "Conversations"], ["contact_exchanges", "CEs"], ["dtms", "DTMs"], ["mg1s", "MG1s"]];
export const PACE = {
  achieved: ["20+ MG1s achieved", "bg-emerald-100 text-emerald-800"],
  on_pace: ["On pace", "bg-emerald-50 text-emerald-700"],
  behind: ["Behind pace", "bg-amber-50 text-amber-800"],
  not_started: ["Starts 1 Nov", "bg-slate-100 text-slate-600"],
  ended: ["Run finished", "bg-slate-100 text-slate-600"],
};
export const VERIFY = {
  verified: ["Leader verified", "bg-emerald-100 text-emerald-800"],
  needs_review: ["Needs re-review", "bg-red-50 text-red-700"],
  unverified: ["Not yet verified", "bg-slate-100 text-slate-600"],
};
export const fmtDay = (d) => new Date(`${d}T12:00:00`).toLocaleDateString("en-AU", { day: "numeric", month: "short" });
const fmt = fmtDay;
export const weekLabel = (w) => `Week ${w.index} · ${w.start === w.end ? fmt(w.start) : `${fmt(w.start)}–${fmt(w.end)}`}${w.partial ? " (partial week)" : ""}`;

export function Pill({ kind, map, testid }) {
  const [label, cls] = map[kind] || ["—", "bg-slate-100 text-slate-600"];
  return <span data-testid={testid} className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap ${cls}`}>{label}</span>;
}

export function Bar({ value, max }) {
  return (
    <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
      <div className="h-full bg-[#C9A961] transition-[width] duration-500" style={{ width: `${Math.min(100, (value / max) * 100)}%` }} />
    </div>
  );
}

export function WeekGoals({ w, testid }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid={testid}>
      {GOALS.map(([k, l]) => {
        const left = w.goals[k] - w.actuals[k];
        return (
          <div key={k} className={`rounded-lg border p-2 ${w.met[k] ? "border-emerald-200 bg-emerald-50/60" : "border-slate-200 bg-white"}`}>
            <div className="text-[10px] uppercase tracking-wider text-[#8A6D1F] font-semibold flex items-center gap-1">
              {w.met[k] ? <Check className="w-3 h-3 text-emerald-600" /> : null}{l}
            </div>
            <div className="font-serif text-lg text-[#0A1F44] tabular-nums">{w.actuals[k]}<span className="text-xs text-slate-400"> / {w.goals[k]}</span></div>
            <div className="text-[10px] text-slate-500">{left > 0 ? `${left} to go` : left < 0 ? `${-left} over` : "Goal met"}
              {w.pro_rata ? ` · pro-rata ${w.pro_rata[k]}` : ""}</div>
          </div>
        );
      })}
    </div>
  );
}

export function WeekTable({ weeks }) {
  return (
    <div className="overflow-x-auto -mx-1" data-testid="stp-week-table">
      <table className="w-full text-xs min-w-[34rem]">
        <thead><tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
          <th className="py-1.5 pr-2">Week</th>{GOALS.map(([k, l]) => <th key={k} className="py-1.5 px-1 text-right">{l}</th>)}
          <th className="py-1.5 px-1 text-right">Launches</th><th className="py-1.5 pl-2">Goals</th>
        </tr></thead>
        <tbody className="divide-y divide-slate-100">
          {weeks.map((w) => (
            <tr key={w.index} className={w.state === "current" ? "bg-[#C9A961]/10" : ""} data-testid={`stp-week-${w.index}`}>
              <td className="py-1.5 pr-2 whitespace-nowrap">{weekLabel(w)}{w.state === "current" ? " · now" : ""}</td>
              {GOALS.map(([k]) => (
                <td key={k} className={`py-1.5 px-1 text-right tabular-nums ${w.state !== "future" && w.met[k] ? "text-emerald-700 font-semibold" : ""}`}>
                  {w.state === "future" ? "—" : w.actuals[k]}/{w.goals[k]}
                </td>
              ))}
              <td className="py-1.5 px-1 text-right tabular-nums">{w.state === "future" ? "—" : w.actuals.launches}</td>
              <td className="py-1.5 pl-2">{w.state === "future" ? <span className="text-slate-400">Upcoming</span>
                : w.all_met ? <Check className="w-4 h-4 text-emerald-600" /> : <X className="w-4 h-4 text-slate-300" />}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function StpInfo() {
  return (
    <ul className="text-sm text-slate-700 space-y-1.5 list-disc pl-5 marker:text-[#C9A961]" data-testid="stp-rules">
      <li>All Participants qualify for a special Zoom Call with a Guest Speaker</li>
      <li><b className="text-[#0A1F44]">SPECIAL REWARD:</b> 20+ MG1s or 1 Launch (Private event at Summer Conference for Qualifiers)</li>
      <li>Log all results in your normal conversations section of Hub</li>
    </ul>
  );
}

export function Countdown() {
  const [c, setC] = useState(() => countdown());
  useEffect(() => {
    const tick = () => setC(countdown());
    const id = setInterval(tick, 60000);
    window.addEventListener("focus", tick);
    document.addEventListener("visibilitychange", tick);
    return () => { clearInterval(id); window.removeEventListener("focus", tick); document.removeEventListener("visibilitychange", tick); };
  }, []);
  return (
    <div role="status" aria-live="polite" data-testid="stp-countdown" data-state={c.state}
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-semibold whitespace-nowrap ${c.state === "live" ? "bg-[#0A1F44] text-[#C9A961]" : "bg-slate-100 text-slate-700"}`}>
      <Timer className="w-4 h-4" aria-hidden="true" />{c.label}
    </div>
  );
}
